<template>
    <header class="border-b border-gray-200 bg-gradient-to-br from-slate-50 to-slate-100 py-5">
        <div class="max-w-7xl mx-auto px-5">
            <nav class="flex items-center text-sm text-slate-600 mb-2">
                <button @click="window.history.back()" class="text-slate-700 font-medium transition-colors duration-200">
                    Catégorie
                </button>
                <span class="mx-2 text-slate-400">›</span>
                <span class="text-slate-700">{{ categorie.wording }}</span>
            </nav>
        </div>
    </header>

    <main class="bg-gray-50 py-20">
        <div class="max-w-6xl mx-auto px-4">
            <div class="mb-12 text-center pb-4 border-b border-gray-100">
                <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2A3993] mb-6">
                    {{ categorie.wording }}
                </h1>
                <p class="text-lg text-gray-600 mx-auto max-w-3xl">
                    Parcourez les ouvrages de cette catégorie et laissez-vous inspirer par de nouvelles lectures.
                </p>
            </div>

            <!-- Grille des books avec loading shimmer -->
            <div v-if="isloading" class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                <div v-for="n in 6" :key="n" class="shimmer-card bg-white rounded-xl shadow-lg overflow-hidden">
                    <div class="h-56 bg-gray-200 shimmer"></div>
                    <div class="p-6">
                        <div class="flex justify-between items-center mb-3">
                            <div class="h-4 bg-gray-200 rounded w-24 shimmer"></div>
                            <div class="h-4 bg-gray-200 rounded w-16 shimmer"></div>
                        </div>
                        <div class="h-6 bg-gray-200 rounded mb-3 shimmer"></div>
                        <div class="h-6 bg-gray-200 rounded mb-6 w-3/4 shimmer"></div>
                        <div class="space-y-2 mb-6">
                            <div class="h-4 bg-gray-200 rounded shimmer"></div>
                            <div class="h-4 bg-gray-200 rounded shimmer"></div>
                            <div class="h-4 bg-gray-200 rounded w-2/3 shimmer"></div>
                        </div>
                        <div class="h-12 bg-gray-200 rounded-lg shimmer"></div>
                    </div>
                </div>
            </div>

            <!-- Grille des books réelles -->
            <div v-else-if="!isloading && books" class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div
                    v-for="book in books"
                    :key="book.id"
                    @click="goToBook(book)"
                    class="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500
                    hover:-translate-y-2 group cursor-pointer border border-gray-100 hover:border-[#ff7e41]/30"
                >
                    <div class="relative overflow-hidden">
                        <img
                            :src="book.banners"
                            :alt="book.title"
                            class="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                        />

                        <!-- Badge genre -->
                        <div class="absolute top-3 left-3">
              <span
                  :class="book.book_type"
                  class="px-3 py-1 text-white text-xs font-bold rounded-full shadow-lg"
              >
                {{ book.book_type }}
              </span>
                        </div>

                        <!-- Note/Rating -->
                        <div class="absolute top-3 right-3 bg-white/90 px-2 py-1 rounded-full">
                            <div class="flex items-center space-x-1">
                                <svg class="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                                <span class="text-xs font-bold text-gray-700">{{ book.evaluation || '0' }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="p-5">
                        <h3 class="font-bold text-gray-900 mb-2 line-clamp-2 text-lg group-hover:text-[#ff7e41] transition-colors">
                            {{ book.title }}
                        </h3>

                        <p class="text-gray-600 text-sm mb-3 font-medium">{{ book.authors }}</p>

                        <div class="flex items-center justify-between text-xs text-gray-500">
              <span class="flex items-center space-x-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                </svg>
                <span>{{ book.pages }} pages</span>
              </span>
                            <span class="font-semibold text-[#ff7e41]">{{ book.book_type || 'Non défini' }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Message si aucune book trouvée -->
            <div v-if="!isloading && !books" class="text-center py-16">
                <div class="max-w-md mx-auto">
                    <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">Aucun ouvrage trouvé pour cette catégorie</h3>
                    <p class="text-gray-500">Essayez de modifier vos critères de recherche.</p>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup>
import { route } from "@/helper.js";
import { onMounted, ref } from "vue";
import { NotificationToast } from "@/utils/notification.js";

const isloading = ref(false);
const books = ref([]);

const props = defineProps({
    categorie: Object
});

const goToBook = (book) => {
    window.location.href = route('show-book', { book: book.id,slug:book?.slug });
};

const loadBooks = async () => {
    isloading.value = true;
    try {
        const response = await axios('/api/categorieBook/' + props.categorie.id);
        if (response.data) {
            books.value = response.data.books;
        }
    } catch (error) {
        NotificationToast('error', error.response?.data?.message || 'Erreur lors du chargement');
    } finally {
        isloading.value = false;
    }
};

onMounted(() => {
    loadBooks();
});
</script>

<style scoped>
@keyframes shimmer {
    0% {
        background-position: -200px 0;
    }
    100% {
        background-position: calc(200px + 100%) 0;
    }
}

.shimmer {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
}

.shimmer-card {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.9;
    }
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
